import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { describeCallsError } from '../utils/apiErrorMessage';
import { useLocation } from '@/shared/hooks/useLocation';
import { callsApi } from '../api/callsApi';
import type { CallSourceInput, CallSourceUpdate } from '../api/callsApi';
import { callsKeys } from '../queryKeys';

export function useCallSources() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const query = useQuery({
    queryKey: callsKeys.sources(locationId),
    queryFn: () => callsApi.listSources(locationId),
    enabled: !!locationId,
  });

  return {
    sources: query.data ?? [],
    locationId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useCallSourceActions(locationId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: callsKeys.sources(locationId) });
    queryClient.invalidateQueries({ queryKey: callsKeys.attribution(locationId) });
    queryClient.invalidateQueries({ queryKey: callsKeys.numbers(locationId) });
  };

  const create = useMutation({
    mutationFn: (input: CallSourceInput) => callsApi.createSource(input),
    onSuccess: () => {
      toast.success('Source created — activate it to provision its number');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to create source')),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CallSourceUpdate }) =>
      callsApi.updateSource(id, locationId, data),
    onSuccess: () => {
      toast.success('Source saved');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to save source')),
  });

  const activate = useMutation({
    mutationFn: (id: string) => callsApi.activateSource(id, locationId),
    onSuccess: () => {
      toast.success('Source is live — number provisioned');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to activate source')),
  });

  const pause = useMutation({
    mutationFn: (id: string) => callsApi.pauseSource(id, locationId),
    onSuccess: () => {
      toast.success('Source paused — its number now answers generically');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to pause source')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => callsApi.deleteSource(id, locationId),
    onSuccess: () => {
      toast.success('Source deleted');
      invalidate();
    },
    onError: (err: Error) => toast.error(describeCallsError(err, 'Failed to delete source')),
  });

  return { create, update, activate, pause, remove };
}

export function useSourceAttribution() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const query = useQuery({
    queryKey: callsKeys.attribution(locationId),
    queryFn: () => callsApi.getSourceAttribution(locationId),
    enabled: !!locationId,
  });

  return {
    attribution: query.data ?? null,
    locationId,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
